import { Link } from "react-router-dom";
import { ArrowRight, Heart, CheckCircle2, AlertTriangle, ChevronDown, Mail, Newspaper, Twitter, Facebook, MessageCircle, Share2 } from "lucide-react";
import { useState } from "react";
import { useT } from "../i18n/LanguageContext";
import SiteHeader from "../components/site/SiteHeader";
import SiteFooter from "../components/site/SiteFooter";
import FloatingDonate from "../components/site/FloatingDonate";
import SurvivorStoriesSection from "../components/site/SurvivorStoriesSection";
import {
  IMG_UN_PORTRAIT,
  IMG_UN_TEAM,
  MUSIC_VIDEO,
  IMG_MOTHER_DAUGHTER,
  IMG_NEXT_GEN,
  IMG_WORKSHOP_HERO,
  IMG_WORKSHOP_BAGS,
  IMG_WORKSHOP_BANNER,
  IMG_WORKSHOP_GROUP,
  IMG_WORKSHOP_TALK,
  IMG_WORKSHOP_VILLAGE,
  IMG_COMMUNITY_AFTER,
  IMG_PRESS,
  PRESS_FR,
  PRESS_EN,
  PRESS_WOMEN_OF_SOLUTIONS,
  IMG_LABOR_DAY_1,
  IMG_LABOR_DAY_2,
  IMG_LABOR_DAY_3,
  IMG_LABOR_DAY_4,
  IMG_LABOR_DAY_5,
  STOP_FGM,
  SHARE_URL,
  CONTACT,
} from "../components/site/siteAssets";

const copy = {
  en: {
    back: "Selector",
    nav: { story: "The Story", how: "Programs", shop: "Press", give: "Donate" },
    kicker: "Mother Hippo Foundation",
    heroA: "If we don't speak up,",
    heroB: "who will?",
    heroSub: "Every 11 seconds, a girl is cut. More than 200 million women alive today carry what should have never happened. Mother Hippo Foundation, founded by survivor Sélifa Boukari McGreevy, is here to end FGM in our lifetime — beginning in Togo.",
    heroDonate: "Donate",
    heroWatch: "Watch the film",
    missionKicker: "The Mother Hippo Project",
    missionP1: "My name is Sélifa Boukari McGreevy. I was born in Sokodé, central Togo. For the past 25 years, I have lived in Las Vegas, Nevada, as a U.S. citizen alongside my American husband.",
    missionP2: "I am the proud founder of the Mother Hippo Foundation, based in Las Vegas — a nonprofit dedicated to empowering women and promoting healing across Africa and diaspora communities. The Hippo represents not only Togo but a sacred symbol of Africa: hippo wisdom is healing, protection of family, and the birth of new ideas. So… HEAR US ROAR.",
    // Community grid (replaces Partners)
    communityKicker: "Our community · Togo",
    communityTitle: "Women, daughters, mothers — together.",
    communityBody: "Honoring the women of Togo on Labor Day 2026. The Mother Hippo Foundation is rooted in community: in the villages, the chairs, the chants, the cloth, the voices that refuse silence.",
    communityCta: "See more from our projects",
    donateMonthly: "Donate monthly",
    donateAny: "Donate any amount",
    // PROGRAMS — real content from "Our Projects" page
    howKicker: "Mission Against FGM · April 1–4, 2026",
    howHead: "Togo Workshops 2026.",
    howSub: "Three villages. One movement.",
    howIntro: "In April 2026, the Mother Hippo Foundation carried out a series of impactful community workshops in Togo, reaching the villages of Tindjassi Sarakawa, Djarkpanga, and Boulohou. These workshops were led by a courageous team of women — many of them survivors of female genital mutilation — who are now using their voices to educate, empower, and protect the next generation.",
    howBody: "Through open dialogue, community engagement, and education, the workshops created safe spaces for women, families, and local leaders to confront the realities of FGM and explore pathways toward ending the practice. These efforts are part of a broader mission to eliminate FGM not only in Togo, but across Africa and around the world.",
    howCloser: "Your donation helps provide education, outreach, and protection for girls at risk, and supports survivors who are leading the fight for a future free from FGM.",
    howStat1: "3 villages",
    howStat1Sub: "Tindjassi Sarakawa · Djarkpanga · Boulohou",
    howStat2: "4 days",
    howStat2Sub: "of dialogue, education, and listening",
    howStat3: "Survivor-led",
    howStat3Sub: "by women who carry the wound — and the wisdom",
    howCaption1: "Women participants proudly display FGM awareness bags following a Mother Hippo Foundation workshop in Togo, supporting education and community action to end FGM.",
    howCaption2: "Workshop participants gather behind an advocacy banner calling for an end to female genital mutilation, reinforcing a shared commitment to protect future generations.",
    gift100: "100%",
    gift100Sub: "of your donation funds programs. Operating costs are covered by dedicated patrons.",
    giveWater: "Give Choice",
    // PRESS section (replaces Shop)
    // PRESS / AS SEEN IN
    shopKicker: "As seen in · 2017 — 2026",
    shopTitle: "The world is listening.",
    shopBody: "From local newspapers in 2017 to international magazines in 2026 — survivor voices, finally being heard.",
    shopGifts: "Read the original story",
    pressLvKicker: "Las Vegas Review-Journal · 2017",
    pressLvTitle: "\"Las Vegas woman fights to stop female genital mutilation.\"",
    pressWomenKicker: "Women of Solutions Magazine · March 2026",
    pressWomenTitle: "Special Women's Month edition — The Year of Impact.",
    pressEnKicker: "International feature · 2026",
    pressEnTitle: "A survivor who became a global voice against female genital mutilation.",
    pressFrKicker: "Édition francophone · 2026",
    pressFrTitle: "Briser le silence, sauver des vies.",
    pressRead: "Read",
    numbersKicker: "This week",
    numbersA: "Every 11s",
    numbersB: "a girl somewhere is cut. We are racing against that clock — through 12 Togolese cities, 18 African countries, and one shared commitment to end FGM by 2030.",
    learnMore: "Learn more",
    silenceKicker: "Who Are We? · 2026 Advocacy Campaign",
    silenceHead: "Silence is not an option.",
    silenceWarn: "Content warning · The following statement describes the act of Female Genital Mutilation in graphic, first-person detail. Please proceed with care.",
    silenceRead: "Read the statement",
    silenceHide: "Hide statement",
    silenceIntro: "I respectfully ask that you take a few minutes of your time to read the statement for our 2026 advocacy campaign music against Female Genital Mutilation song.",
    silenceBody: [
      "I ask everybody here to close their eyes for a moment and imagine yourself as a happy 8 year old girl who has a bright future and happiness ahead of her. Then your mother, who's duty it should be to protect you, wakes you up very early in the morning without any food in your stomach and asks you to come with her.",
      "She takes you to a strange house where you have never been, and when you arrive there your mother pushes you into line alongside your older sister, your female cousins and many girls from different areas of your village who are surrounded by their own mothers. You feel confused by the situation, you are in fact terrified and your heart is beating so fast you don't know what to do, so you started crying.",
      "You have no control over this, and one by one you are asked to enter an open shower where you face six large women who are going to change your life forever. These women ask each of you to take off your clothes and lie down on the cement floor, and when you do two women hold down your hands side by side while two other women spread your legs and hold them down very tightly. One woman sits on your chest and covers your face and mouth with a thick cloth so that you can not see anything nor cry out.",
      "You are suffocated trying to breathe under the thick cloth and then another woman comes over to squat between your legs holding a sharp knife. She tightly holds the side of your clitoris with her sharp fingernails and places a knife below your clitoris where you feel it slowly slicing through your skin without anesthesia. She continues slicing through your skin deeply until she gets to the other side of your clitoris, and at the same time you have blood running down your thighs and feel excruciating pain from your head to toe from the sharp knife slicing through your skin.",
      "You scream under the thick cloth begging for compassion but no one will save you. Even your own mother who is standing there watching her own flesh and blood being tortured has no empathy or remorse for you whatsoever. Your happiness as that 8 year old has come to an end. You will never experience the same happiness again in your life.",
    ],
    silenceVideoLabel: "PLEASE WATCH THE MUSIC VIDEO OF THE SONG BELOW",
    silenceVideoComing: "Music video — coming soon",
    survivorsKicker: "In their own words · Tindjassi Sarakawa, Togo",
    survivorsHead: "Survivors' Stories",
    survivorsSub: "These accounts are shared by women from the same village where I was mutilated. They are graphic, painful, and sacred. Read with care.",
    survivorLabel: "Survivor",
    survivorShare: {
      label: "Share this story",
      x: "Share on X",
      facebook: "Share on Facebook",
      whatsapp: "Share on WhatsApp",
      email: "Email this story",
      message: (name, place) => `${name}'s story — a survivor speaks. Stand with the Mother Hippo Foundation to end FGM in our lifetime. #EndFGM`,
      emailSubject: (name) => `${name}'s story — Mother Hippo Foundation`,
    },
    survivors: [
      {
        name: "Aridja ZAKARI",
        place: "FGM Survivor · Tindjassi Sarakawa, Togo",
        note: "From the village where Sélifa McGreevy was mutilated",
        quote: "I had it done to me, and thank God I didn't experience any problems, but one of us suffered greatly. She lost blood throughout the day of the excision. When she started feeling dizzy, she was taken to the hospital, and the doctors were able to stop the bleeding. She was never well again until adulthood. Today she is married but cannot conceive. Another example: two girls were circumcised, but one of them died. I will never allow it to be done to my daughters. No matter the financial offer, I will never give in because of what I myself went through.",
      },
      {
        name: "Roubatou TCHEDRE",
        place: "FGM Survivor · Tindjassi Sarakawa, Togo",
        note: "From the village where Sélifa McGreevy was mutilated",
        quote: "Female genital mutilation has caused immense damage. It has claimed the lives of many of our sisters. I myself, as I speak to you, was a victim. The first time it was done to me, it didn't go as they intended. They did it to me a second time, and it was the same. When they decided to do it to me a third time, I fled home to seek refuge with a Kabye chief. That's where they came to get me and bring me back home. And for this third time, it was large men who held me still so they could do it to me. Because of everything I've experienced, I've vowed that whatever the offer, I will never want it done to my daughter.",
      },
      {
        name: "Soumeya Saidou",
        place: "FGM Survivor · Tindjassi Sarakawa, Togo",
        note: "From the village where Sélifa McGreevy was mutilated",
        quote: "Because of female genital mutilation, I have no desire for sex. I no longer consider myself a woman because I have no sexual desire. We demand that this practice be stopped because we have been truly deceived. When they wanted to do it to us, we fiercely opposed it, but our parents forced us. One of us took a great risk; her existence is a miracle from God. I am not yet 40 years old, and yet I feel no sexual desire. One of our friends, who died as a result of this mutilation, had said no, but she was forced, and today she is no longer with us. This is why drastic measures have been taken in our community to discourage perpetrators. A pact has now been signed here, and anyone who betrays it will die without hesitation. Furthermore, we are investigating, and if we discover anyone, we will report them to the police.",
      },
      {
        name: "Awa DAOUDA",
        place: "FGM Survivor · Tindjassi Sarakawa, Togo",
        note: "From the village where Sélifa McGreevy was mutilated",
        quote: "Since my excision, I no longer experience pleasure during sexual intercourse, and no matter how attractive a man is, I feel no attraction.",
      },
      {
        name: "KEGBAO Adjiro Amina",
        place: "65 years old · Wife of TCHAKONDO · Tem ethnicity, Soudou commune, Togo",
        note: "Circumcised at age 9 under social pressure",
        quote: "I was circumcised at the age of 9 under all forms of social pressure — swearing, insults, intimidation, and the glorification of this act. My father was a gendarme who did not want his children to suffer this ordeal. During the school holidays of 1970, I went to see my grandmother in Gandè. She was surrounded by several young girls who had already undergone FGM. They mocked me for having a clitoris; sometimes they even showed me their genitals so I could tell the difference. After all this boasting, born of ignorance, I told my grandmother that I wanted to have it done. I was the only one that day to be circumcised. You can't imagine my joy at no longer being treated like I had a clitoris. Today, I regret this act, which remains and will remain indelible. Only I know my own strength. I will do everything in my power to ensure that there is zero FGM in my community. Thank you to those who want to help me in this fight.",
      },
      {
        name: "Akondo Adjei",
        place: "54 years old · Mother of four boys",
        note: "Circumcised at age 10 in 1982",
        quote: "I was 10 years old, very early one morning in 1982. I was awake at 5 AM, waiting for my cousins and neighbors in our schoolyard. I thought that since I was still young, it wasn't our time yet. To my great surprise, my grandmother called me over for FGM. I had no choice. So there I was, very young, among eight other girls. At that time, it was a source of pride for a girl to be circumcised. After two days, I went through a nightmare. I couldn't urinate. So every morning, very early, they heated water in a large pot, and they made us take turns going in. We showered to dress our wounds, using black soap to clean the wound, and then rinsed with hot water.",
      },
    ],
    pledgeKicker: "Sign the Pledge",
    pledge: [
      "I believe every girl has a human right to her own body.",
      "I believe FGM is violence against children, not cultural tradition.",
      "I believe survivors — like Sélifa — must lead the work to end this.",
      "I believe 200 million women is 200 million reasons to act.",
      "I believe if we don't speak up, no one will.",
    ],
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email Address",
    signPledge: "Sign the Pledge",
    readFull: "Read the Full Pledge",
    shareKicker: "Spread the word",
    shareTitle: "Recruit one friend. End FGM faster.",
    shareSub: "Every voice that joins us shortens the journey to a girl's safety. Share the pledge in 5 seconds.",
    shareMessage: "I just signed Mother Hippo Foundation's pledge to end Female Genital Mutilation. 200 million women carry this wound. Stand with us — sign the pledge:",
    shareEmailSubject: "Stand with Mother Hippo Foundation — sign the pledge to end FGM",
    shareX: "Share on X",
    shareFacebook: "Share on Facebook",
    shareWhatsapp: "Share on WhatsApp",
    shareEmail: "Email a friend",
    shareCopy: "Copy link",
    shareCopied: "Link copied",
    giveClean: "Give",
    giveStrong: "choice,",
    giveEnd: "today.",
    persistent: "Donate",
    // Donation tiers
    donateKicker: "Support our work",
    donateTitle: "Help us launch the campaign.",
    donateSub: "Mother Hippo Foundation is a U.S. 501(c)(3) nonprofit. Your tax-deductible donation will initially fund our 2026 social-media advocacy campaign — hiring the creative and outreach team that will carry survivor voices to every corner of Africa, the diaspora, and global policy rooms.",
    donateSoonKicker: "Donations opening soon",
    donateSoonTitle: "Online giving launches in the coming weeks.",
    donateSoonBody: "We're finalising our payment partners. In the meantime, email us — we'll send you bank, Zelle, or cheque instructions and confirm your tax-deductible receipt within 24 hours.",
    donateSoonCta: "Email to give",
    donateSoonReply: "We reply within 24 hours.",
    // Footer
    footerAddressTitle: "Visit",
    footerAddress: "4132 Rainbow Blvd, Suite #217\nLas Vegas, Nevada 89103",
    footerEmailTitle: "Email",
    footerEmail: "info@motherhippo.org",
    footerPhoneTitle: "Call",
    footerPhone: "+1 (702) 782-2263",
    footerLocations: "Las Vegas, Nevada · Sokodé, Togo",
    rights: "© 2026 Mother Hippo Foundation. A U.S. 501(c)(3) nonprofit. All rights reserved.",
  },
  fr: {
    back: "Sélecteur",
    nav: { story: "L'Histoire", how: "Programmes", shop: "Presse", give: "Donner" },
    kicker: "Mother Hippo Foundation",
    heroA: "Si nous ne parlons pas,",
    heroB: "qui le fera ?",
    heroSub: "Toutes les 11 secondes, une fille est excisée. Plus de 200 millions de femmes vivantes aujourd'hui portent ce qui n'aurait jamais dû arriver. La Mother Hippo Foundation, fondée par la survivante Sélifa Boukari McGreevy, est là pour mettre fin aux MGF de notre vivant — à commencer par le Togo.",
    heroDonate: "Faire un don",
    heroWatch: "Regarder le film",
    missionKicker: "Le Projet Mother Hippo",
    missionP1: "Je m'appelle Sélifa Boukari McGreevy. Je suis née à Sokodé, au centre du Togo. Depuis 25 ans, je vis à Las Vegas, au Nevada, en tant que citoyenne américaine aux côtés de mon mari américain.",
    missionP2: "Je suis la fière fondatrice de la Mother Hippo Foundation, basée à Las Vegas — une organisation à but non lucratif dédiée à autonomiser les femmes et à promouvoir la guérison à travers l'Afrique et les communautés de la diaspora. L'hippopotame représente non seulement le Togo mais aussi un symbole sacré de l'Afrique : la sagesse de l'hippopotame, c'est la guérison, la protection de la famille et la naissance d'idées nouvelles. Alors… ENTENDEZ-NOUS RUGIR.",
    communityKicker: "Notre communauté · Togo",
    communityTitle: "Femmes, filles, mères — ensemble.",
    communityBody: "Honorer les femmes du Togo lors de la Fête du Travail 2026. La Mother Hippo Foundation est enracinée dans la communauté : dans les villages, les chaises, les chants, les pagnes, les voix qui refusent le silence.",
    communityCta: "Voir nos projets",
    donateMonthly: "Don mensuel",
    donateAny: "Don libre",
    // PROGRAMMES — contenu réel de "Our Projects"
    howKicker: "Mission contre les MGF · 1–4 avril 2026",
    howHead: "Ateliers du Togo 2026.",
    howSub: "Trois villages. Un mouvement.",
    howIntro: "En avril 2026, la Mother Hippo Foundation a mené une série d'ateliers communautaires marquants au Togo, dans les villages de Tindjassi Sarakawa, Djarkpanga et Boulohou. Ces ateliers ont été conduits par une équipe courageuse de femmes — dont beaucoup sont des survivantes des MGF — qui utilisent désormais leur voix pour éduquer, autonomiser et protéger la prochaine génération.",
    howBody: "À travers le dialogue ouvert, l'engagement communautaire et l'éducation, les ateliers ont créé des espaces sûrs où les femmes, les familles et les responsables locaux ont pu affronter la réalité des MGF et explorer les chemins pour mettre fin à cette pratique. Ces efforts s'inscrivent dans une mission plus large : éliminer les MGF non seulement au Togo, mais à travers l'Afrique et le monde entier.",
    howCloser: "Votre don nous permet d'offrir éducation, sensibilisation et protection aux jeunes filles à risque, et soutient les survivantes qui mènent ce combat pour un avenir libéré des MGF.",
    howStat1: "3 villages",
    howStat1Sub: "Tindjassi Sarakawa · Djarkpanga · Boulohou",
    howStat2: "4 jours",
    howStat2Sub: "de dialogue, d'éducation et d'écoute",
    howStat3: "Mené par les survivantes",
    howStat3Sub: "des femmes qui portent la blessure — et la sagesse",
    howCaption1: "Des participantes brandissent fièrement les sacs de sensibilisation aux MGF à l'issue d'un atelier de la Mother Hippo Foundation au Togo, à l'appui de l'éducation et de l'action communautaire.",
    howCaption2: "Les participantes se rassemblent derrière une banderole appelant à la fin des MGF, renforçant l'engagement commun à protéger les générations futures.",
    gift100: "100 %",
    gift100Sub: "de votre don finance les programmes. Les frais de fonctionnement sont couverts par des mécènes dédiés.",
    giveWater: "Offrir le Choix",
    // PRESSE
    // PRESSE / VU DANS
    shopKicker: "Vu dans · 2017 — 2026",
    shopTitle: "Le monde écoute.",
    shopBody: "Des journaux locaux en 2017 aux magazines internationaux en 2026 — la voix des survivantes, enfin entendue.",
    shopGifts: "Lire l'article original",
    pressLvKicker: "Las Vegas Review-Journal · 2017",
    pressLvTitle: "« Une femme de Las Vegas se bat pour mettre fin aux MGF. »",
    pressWomenKicker: "Women of Solutions Magazine · Mars 2026",
    pressWomenTitle: "Édition spéciale Mois de la femme — L'année de l'impact.",
    pressEnKicker: "Reportage international · 2026",
    pressEnTitle: "Une survivante devenue voix mondiale contre les mutilations génitales féminines.",
    pressFrKicker: "Édition francophone · 2026",
    pressFrTitle: "Briser le silence, sauver des vies.",
    pressRead: "Lire",
    numbersKicker: "Cette semaine",
    numbersA: "Toutes les 11s",
    numbersB: "une fille est excisée quelque part. Nous courons contre cette horloge — à travers 12 villes togolaises, 18 pays africains et un engagement commun : éradiquer les MGF d'ici 2030.",
    learnMore: "En savoir plus",
    silenceKicker: "Qui sommes-nous ? · Campagne de plaidoyer 2026",
    silenceHead: "Le silence n'est pas une option.",
    silenceWarn: "Avertissement · La déclaration suivante décrit les MGF en détail à la première personne. Lecture éprouvante — prenez soin de vous.",
    silenceRead: "Lire la déclaration",
    silenceHide: "Masquer la déclaration",
    silenceIntro: "Je vous demande respectueusement de prendre quelques minutes pour lire la déclaration de la campagne musicale 2026 contre les mutilations génitales féminines.",
    silenceBody: [
      "Je demande à chacun·e ici de fermer les yeux un instant et d'imaginer une petite fille de 8 ans, heureuse, avec un avenir radieux et le bonheur devant elle. Puis sa mère — celle dont le devoir est de la protéger — la réveille très tôt le matin, le ventre vide, et lui demande de la suivre.",
      "Elle l'emmène dans une maison étrange où elle n'est jamais allée, et à l'arrivée, sa mère la pousse en file aux côtés de sa grande sœur, de ses cousines et de nombreuses filles de différents quartiers du village, toutes entourées de leurs propres mères. Elle se sent perdue, terrifiée, son cœur bat si vite qu'elle ne sait que faire — elle se met à pleurer.",
      "Elle n'a aucun contrôle sur ce qui suit, et une à une, les filles entrent dans une douche ouverte où six grandes femmes les attendent — celles qui vont changer leur vie à jamais. Elles demandent à chaque fille d'enlever ses vêtements et de s'allonger sur le sol en ciment. Deux femmes lui maintiennent les mains, deux autres lui écartent et plaquent les jambes au sol. Une autre s'assoit sur sa poitrine et lui couvre le visage et la bouche d'un tissu épais pour qu'elle ne puisse ni voir, ni crier.",
      "Elle suffoque sous le tissu, et une autre femme s'accroupit entre ses jambes avec un couteau aiguisé. Elle saisit fermement le côté de son clitoris avec ses ongles tranchants et passe la lame qui tranche lentement sa peau, sans anesthésie. Elle continue de couper profondément jusqu'à l'autre côté du clitoris. Le sang coule le long de ses cuisses, et la douleur est insupportable, de la tête aux pieds.",
      "Elle hurle sous le tissu, suppliant qu'on l'épargne, mais personne ne la sauvera. Même sa propre mère, qui regarde sa chair et son sang être torturé·e·s, ne ressent aucune empathie, aucun remords. Le bonheur de cette enfant de 8 ans s'arrête là. Elle ne connaîtra plus jamais le même bonheur.",
    ],
    silenceVideoLabel: "REGARDEZ LE CLIP MUSICAL CI-DESSOUS",
    silenceVideoComing: "Clip musical — bientôt disponible",
    survivorsKicker: "Dans leurs propres mots · Tindjassi Sarakawa, Togo",
    survivorsHead: "Récits de Survivantes",
    survivorsSub: "Ces témoignages sont partagés par des femmes du village où j'ai moi-même été mutilée. Ils sont crus, douloureux, sacrés. À lire avec soin.",
    survivorLabel: "Survivante",
    survivorShare: {
      label: "Partager ce témoignage",
      x: "Partager sur X",
      facebook: "Partager sur Facebook",
      whatsapp: "Partager sur WhatsApp",
      email: "Envoyer par e-mail",
      message: (name, place) => `Le témoignage de ${name} — une survivante prend la parole. Soutenez la Mother Hippo Foundation pour mettre fin aux MGF de notre vivant. #EndFGM`,
      emailSubject: (name) => `Témoignage de ${name} — Mother Hippo Foundation`,
    },
    survivors: [
      {
        name: "Aridja ZAKARI",
        place: "Survivante des MGF · Tindjassi Sarakawa, Togo",
        note: "Du village où Sélifa McGreevy a été mutilée",
        quote: "On me l'a fait, et grâce à Dieu je n'ai eu aucun problème, mais l'une d'entre nous a beaucoup souffert. Elle a perdu beaucoup de sang tout au long de la journée. Quand elle a commencé à avoir des vertiges, on l'a emmenée à l'hôpital où les médecins ont pu arrêter l'hémorragie. Elle n'a plus jamais été bien jusqu'à l'âge adulte. Aujourd'hui elle est mariée mais ne peut pas concevoir. Autre exemple : deux filles ont été excisées, mais l'une d'elles est morte. Jamais je ne laisserai cela être fait à mes filles.",
      },
      {
        name: "Roubatou TCHEDRE",
        place: "Survivante des MGF · Tindjassi Sarakawa, Togo",
        note: "Du village où Sélifa McGreevy a été mutilée",
        quote: "Les MGF ont causé d'immenses dégâts. Elles ont coûté la vie à beaucoup de nos sœurs. Moi-même, j'ai été victime. La première fois qu'on me l'a fait, ça ne s'est pas passé comme prévu. Ils me l'ont refait une deuxième fois, sans succès. Quand ils ont décidé de me le faire une troisième fois, j'ai fui chez un chef Kabyé. C'est là qu'ils sont venus me chercher. Cette troisième fois, ce sont des hommes adultes qui m'ont immobilisée. À cause de tout ce que j'ai vécu, j'ai juré : quelle que soit l'offre, je ne voudrai jamais que cela soit fait à ma fille.",
      },
      {
        name: "Soumeya Saidou",
        place: "Survivante des MGF · Tindjassi Sarakawa, Togo",
        note: "Du village où Sélifa McGreevy a été mutilée",
        quote: "À cause des MGF, je n'ai aucun désir sexuel. Je ne me considère plus comme une femme. Nous exigeons que cette pratique cesse car nous avons été véritablement trompées. Je n'ai pas encore 40 ans, et je ne ressens aucun désir. L'une de nos amies, qui est morte de cette mutilation, avait dit non, mais on l'a forcée. Un pacte a été signé ici, et quiconque le trahit en mourra sans hésitation.",
      },
      {
        name: "Awa DAOUDA",
        place: "Survivante des MGF · Tindjassi Sarakawa, Togo",
        note: "Du village où Sélifa McGreevy a été mutilée",
        quote: "Depuis mon excision, je n'éprouve plus de plaisir durant les rapports sexuels, et aussi attirant qu'un homme puisse être, je ne ressens aucune attraction.",
      },
      {
        name: "KEGBAO Adjiro Amina",
        place: "65 ans · Épouse de TCHAKONDO · Ethnie Tem, commune de Soudou, Togo",
        note: "Excisée à 9 ans sous pression sociale",
        quote: "J'ai été excisée à 9 ans sous toutes les formes de pression sociale — jurons, insultes, intimidation, glorification de cet acte. Mon père était gendarme et ne voulait pas que ses enfants subissent cette épreuve. Pendant les vacances scolaires de 1970, je suis allée voir ma grand-mère à Gandè. Elle était entourée de plusieurs jeunes filles qui avaient déjà subi les MGF. Elles se moquaient de moi parce que j'avais un clitoris. Après toutes ces vantardises nées de l'ignorance, j'ai dit à ma grand-mère que je voulais le faire. Aujourd'hui, je regrette cet acte qui restera ineffaçable. Je ferai tout pour qu'il y ait zéro MGF dans ma communauté.",
      },
      {
        name: "Akondo Adjei",
        place: "54 ans · Mère de quatre garçons",
        note: "Excisée à 10 ans en 1982",
        quote: "J'avais 10 ans, très tôt un matin de 1982. À ma grande surprise, ma grand-mère m'a appelée pour les MGF. Je n'avais pas le choix. Au bout de deux jours, j'ai vécu un cauchemar. Je ne pouvais pas uriner. Alors chaque matin, ils chauffaient de l'eau dans une grande marmite et nous y faisaient passer à tour de rôle pour soigner nos plaies au savon noir.",
      },
    ],
    pledgeKicker: "Signer l'Engagement",
    pledge: [
      "Je crois que chaque fille a le droit humain à son propre corps.",
      "Je crois que les MGF sont une violence faite aux enfants, pas une tradition culturelle.",
      "Je crois que les survivantes — comme Sélifa — doivent mener ce combat.",
      "Je crois que 200 millions de femmes représentent 200 millions de raisons d'agir.",
      "Je crois que si nous ne parlons pas, personne ne le fera.",
    ],
    firstName: "Prénom",
    lastName: "Nom",
    email: "Adresse courriel",
    signPledge: "Signer l'Engagement",
    readFull: "Lire l'engagement complet",
    shareKicker: "Faites passer le mot",
    shareTitle: "Recrutez une amie. Mettons fin aux MGF plus vite.",
    shareSub: "Chaque voix qui nous rejoint raccourcit le chemin vers la sécurité d'une fille. Partagez l'engagement en 5 secondes.",
    shareMessage: "Je viens de signer l'engagement de la Mother Hippo Foundation pour mettre fin aux mutilations génitales féminines. 200 millions de femmes portent cette blessure. Rejoignez-nous — signez l'engagement :",
    shareEmailSubject: "Rejoignez la Mother Hippo Foundation — signez l'engagement contre les MGF",
    shareX: "Partager sur X",
    shareFacebook: "Partager sur Facebook",
    shareWhatsapp: "Partager sur WhatsApp",
    shareEmail: "Envoyer à une amie",
    shareCopy: "Copier le lien",
    shareCopied: "Lien copié",
    giveClean: "Offrir",
    giveStrong: "le choix,",
    giveEnd: "aujourd'hui.",
    persistent: "Donner",
    donateKicker: "Soutenir notre travail",
    donateTitle: "Aidez-nous à lancer la campagne.",
    donateSub: "La Mother Hippo Foundation est une association à but non lucratif américaine 501(c)(3). Votre don déductible des impôts financera dans un premier temps notre campagne de plaidoyer 2026 sur les réseaux sociaux — recrutement de l'équipe créative et de terrain qui portera la voix des survivantes en Afrique, dans la diaspora et dans les enceintes politiques mondiales.",
    donateSoonKicker: "Dons bientôt disponibles",
    donateSoonTitle: "Le don en ligne ouvre dans les prochaines semaines.",
    donateSoonBody: "Nous finalisons nos partenaires de paiement. En attendant, écrivez-nous — nous vous enverrons les instructions de virement, Zelle ou chèque et confirmerons votre reçu fiscal sous 24 heures.",
    donateSoonCta: "Écrire pour donner",
    donateSoonReply: "Nous répondons sous 24 heures.",
    footerAddressTitle: "Adresse",
    footerAddress: "4132 Rainbow Blvd, Suite #217\nLas Vegas, Nevada 89103",
    footerEmailTitle: "Courriel",
    footerEmail: "info@motherhippo.org",
    footerPhoneTitle: "Téléphone",
    footerPhone: "+1 (702) 782-2263",
    footerLocations: "Las Vegas, Nevada · Sokodé, Togo",
    rights: "© 2026 Mother Hippo Foundation. Association à but non lucratif américaine 501(c)(3). Tous droits réservés.",
  },
};

export default function PrototypeStories() {
  const t = useT(copy);
  const [silenceOpen, setSilenceOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — fall back to selection prompt
      window.prompt("Copy this link", SHARE_URL);
    }
  };

  return (
    <div
      data-testid="prototype-stories"
      className="min-h-screen bg-[#F4EDE0] text-[#2A1F15] selection:bg-[#8C5A3C] selection:text-white"
      style={{ fontFamily: "'Figtree', 'Work Sans', sans-serif" }}
    >
      <SiteHeader />

      {/* HERO — image collage */}
      <section className="relative bg-[#2A1F15]">
        <div className="relative h-[70vh] min-h-[560px] overflow-hidden">
          {/* Collage — Togo community + survivors */}
          <div className="absolute inset-0 grid grid-cols-3">
            <img src={IMG_LABOR_DAY_2} alt="Mother Hippo community gathering, Togo Labor Day 2026" className="h-full w-full object-cover" />
            <img src={IMG_NEXT_GEN} alt="Girls in Togo with Mother Hippo Foundation" className="h-full w-full object-cover" />
            <img src={IMG_LABOR_DAY_4} alt="Women honored at Mother Hippo Labor Day gathering, Togo" className="h-full w-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#2A1F15]/30 via-[#2A1F15]/50 to-[#2A1F15]/80" />
          <div className="relative z-10 h-full flex flex-col justify-end max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#F4EDE0] font-bold mh-rise">
              {t.kicker}
            </div>
            <h1
              data-testid="stories-hero-title"
              className="mh-rise mh-delay-1 mt-5 font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1] max-w-4xl"
              style={{ fontFamily: "'Merriweather', 'Playfair Display', serif", fontWeight: 700 }}
            >
              {t.heroA}<br />
              <span className="italic text-[#D4A93B]">{t.heroB}</span>
            </h1>
            <p className="mh-rise mh-delay-2 mt-6 max-w-xl text-base md:text-lg text-white/90 leading-relaxed">
              {t.heroSub}
            </p>
          </div>
          <div className="absolute top-6 right-6 text-[10px] tracking-[0.35em] uppercase text-white/80 z-10">
            Scroll ↓
          </div>
        </div>
      </section>

      {/* STOP FGM Campaign Banner */}
      <section className="bg-white py-10 md:py-16 border-y-2 border-[#D4A93B]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <img
            src={STOP_FGM}
            alt="STOP Female Genital Mutilation — Mother Hippo Foundation campaign banner"
            data-testid="stories-stop-fgm-banner"
            className="w-full h-auto rounded-lg shadow-xl"
          />
        </div>
      </section>

      {/* Founder at UN — credibility */}
      <section id="founder" className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 md:col-span-5">
            <img
              src={IMG_UN_PORTRAIT}
              alt="Sélifa Boukari McGreevy at the United Nations"
              className="w-full aspect-square object-cover rounded-lg shadow-2xl"
              data-testid="stories-founder-portrait"
            />
          </div>
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold">Meet the founder</div>
            <h2
              className="mt-4 text-4xl md:text-5xl leading-[1.05]"
              style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
            >
              Sélifa Boukari McGreevy
            </h2>
            <p className="mt-3 text-lg text-[#8C5A3C] italic" style={{ fontFamily: "'Merriweather', serif" }}>
              Founder & Chairwoman · FGM Survivor · Advocate at the United Nations
            </p>
            <p
              className="mt-6 text-base md:text-lg leading-relaxed text-[#2A1F15]/85"
              style={{ fontFamily: "'Merriweather', serif" }}
            >
              From Sokodé to the United Nations General Assembly, Sélifa has spent
              decades carrying the voices of survivors into rooms where decisions
              are made. Mother Hippo Foundation is the home she built so that no
              girl has to walk her road alone.
            </p>
          </div>
        </div>
      </section>

      {/* UN Delegation — at the General Assembly */}
      <section className="py-20 md:py-28 bg-[#2A1F15] text-[#F4EDE0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 md:col-span-7 order-2 md:order-1">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">United Nations · General Assembly</div>
            <h2
              className="mt-4 text-3xl md:text-4xl leading-[1.1]"
              style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
            >
              Bringing Togo's voices to the world stage.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#F4EDE0]/90" style={{ fontFamily: "'Merriweather', serif" }}>
              Mother Hippo Foundation represents survivors and advocates at the
              United Nations and across global FGM coalitions. Our delegation
              ensures that Togolese voices — and every voice silenced by FGM —
              are heard where policy is shaped.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 order-1 md:order-2">
            <img
              src={IMG_UN_TEAM}
              alt="Mother Hippo Foundation delegation at the United Nations General Assembly"
              className="w-full aspect-[4/3] object-cover rounded-lg shadow-2xl"
              data-testid="stories-un-delegation"
            />
          </div>
        </div>
      </section>

      {/* Our Community — photo grid honoring women in Togo */}
      <section id="community" className="py-20 md:py-28 bg-white border-y-2 border-[#D4A93B]/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold" data-testid="stories-community-kicker">{t.communityKicker}</div>
            <h2
              className="mt-4 text-4xl md:text-5xl leading-[1.05]"
              style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
              data-testid="stories-community-title"
            >
              {t.communityTitle}
            </h2>
            <p
              className="mt-5 text-base md:text-lg text-[#2A1F15]/80 leading-relaxed"
              style={{ fontFamily: "'Merriweather', serif" }}
            >
              {t.communityBody}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="stories-community-grid">
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#D4A93B] shadow-md">
              <img src={IMG_LABOR_DAY_1} alt="Mother Hippo Foundation Togo community" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#E3282B] shadow-md md:row-span-2 md:aspect-auto">
              <img src={IMG_LABOR_DAY_3} alt="Women honored at Mother Hippo Labor Day gathering, Togo" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#00A651] shadow-md">
              <img src={IMG_LABOR_DAY_2} alt="Mother Hippo community in Togo" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#8C5A3C] shadow-md">
              <img src={IMG_LABOR_DAY_4} alt="Mother Hippo community advocacy, Togo" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#2A1F15] shadow-md">
              <img src={IMG_LABOR_DAY_5} alt="Mother Hippo Foundation voice in the community" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/our-projects"
              data-testid="stories-community-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2A1F15] text-[#F4EDE0] font-bold hover:bg-[#E3282B] transition-colors shadow-md"
            >
              {t.communityCta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section id="story" className="py-24 md:py-32 max-w-[1100px] mx-auto px-6 md:px-10 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold">
          <span className="h-px w-10 bg-[#D4A93B]" />
          {t.missionKicker}
          <span className="h-px w-10 bg-[#D4A93B]" />
        </div>
        <div
          className="mt-8 space-y-6 text-lg md:text-xl leading-relaxed text-[#2A1F15]/85"
          style={{ fontFamily: "'Merriweather', serif" }}
        >
          <p>{t.missionP1}</p>
          <p>{t.missionP2}</p>
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            href="#donate"
            data-testid="stories-donate-monthly"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#00A651] text-white font-bold hover:bg-[#2A1F15] transition-colors"
          >
            {t.donateMonthly}
          </a>
          <a
            href="#donate"
            data-testid="stories-donate-any"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#E3282B] text-white font-bold hover:bg-[#8C5A3C] transition-colors"
          >
            {t.donateAny}
          </a>
        </div>
      </section>

      {/* PROGRAMS — Togo Workshops April 2026 */}
      <section id="how" className="py-20 md:py-28 bg-white border-y-4 border-[#D4A93B]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold" data-testid="stories-programs-kicker">{t.howKicker}</div>
            <h2
              className="mt-4 text-4xl md:text-6xl leading-[1.05]"
              style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
              data-testid="stories-programs-title"
            >
              {t.howHead}
            </h2>
            <p
              className="mt-3 italic text-2xl md:text-3xl text-[#8C5A3C]"
              style={{ fontFamily: "'Merriweather', serif" }}
            >
              {t.howSub}
            </p>
          </div>

          {/* Hero workshop image */}
          <div className="mt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <div className="aspect-[16/10] overflow-hidden rounded-xl border-4 border-[#2A1F15] shadow-2xl">
                <img src={IMG_WORKSHOP_HERO} alt="Mother Hippo Foundation workshop in Togo, April 2026" className="w-full h-full object-cover" data-testid="stories-programs-hero-img" />
              </div>
              <p className="mt-3 text-xs italic text-[#2A1F15]/65 leading-relaxed">{t.howCaption2}</p>
            </div>
            <div className="col-span-12 md:col-span-4 flex flex-col gap-4">
              <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#00A651]">
                <img src={IMG_WORKSHOP_BAGS} alt="Women holding FGM awareness bags after a Mother Hippo workshop" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#D4A93B]">
                <img src={IMG_WORKSHOP_BANNER} alt="Workshop participants behind an end-FGM advocacy banner" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { big: t.howStat1, small: t.howStat1Sub, color: "#E3282B" },
              { big: t.howStat2, small: t.howStat2Sub, color: "#00A651" },
              { big: t.howStat3, small: t.howStat3Sub, color: "#D4A93B" },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`stories-program-stat-${i}`}
                className="rounded-xl border-2 border-[#2A1F15]/10 bg-[#F4EDE0] p-6 md:p-8"
              >
                <div
                  className="text-3xl md:text-4xl"
                  style={{ fontFamily: "'Merriweather', serif", fontWeight: 700, color: s.color }}
                >
                  {s.big}
                </div>
                <div className="mt-2 text-sm md:text-base text-[#2A1F15]/80 leading-relaxed">{s.small}</div>
              </div>
            ))}
          </div>

          {/* Body copy */}
          <div className="mt-16 grid grid-cols-12 gap-10 items-start">
            <div className="col-span-12 md:col-span-7 space-y-5 text-base md:text-lg leading-[1.85] text-[#2A1F15]/90" style={{ fontFamily: "'Merriweather', serif" }}>
              <p data-testid="stories-programs-intro">{t.howIntro}</p>
              <p>{t.howBody}</p>
              <p className="text-[#8C5A3C] font-semibold italic">{t.howCloser}</p>
              <a
                href="#donate"
                data-testid="stories-programs-donate"
                className="mt-4 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#E3282B] text-white font-bold hover:bg-[#00A651] transition-colors shadow-lg"
              >
                {t.heroDonate} <ArrowRight size={16} />
              </a>
            </div>
            <div className="col-span-12 md:col-span-5 grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img src={IMG_WORKSHOP_GROUP} alt="Women gather at a Togo workshop" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-lg mt-8">
                <img src={IMG_WORKSHOP_TALK} alt="Survivor speaks at a workshop" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img src={IMG_WORKSHOP_VILLAGE} alt="Village participants in Togo" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-lg mt-8">
                <img src={IMG_COMMUNITY_AFTER} alt="Community gathering after a workshop" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 100% give */}
      <section id="give" className="py-24 md:py-32 bg-[#2A1F15] text-[#F4EDE0] relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full blur-3xl opacity-40"
          style={{ background: "radial-gradient(circle, #D4A93B 0%, transparent 60%)" }}
        />
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center relative">
          <div
            className="font-serif text-8xl md:text-9xl leading-none text-[#D4A93B]"
            style={{ fontFamily: "'Merriweather', serif", fontWeight: 900 }}
            data-testid="stories-100-percent"
          >
            {t.gift100}
          </div>
          <p className="mt-6 max-w-2xl mx-auto text-lg md:text-xl text-[#F4EDE0]/90 leading-relaxed">
            {t.gift100Sub}
          </p>
          <a
            href="#pledge"
            data-testid="stories-give-water-cta"
            className="mt-10 inline-flex items-center gap-3 px-10 py-5 rounded-full bg-[#E3282B] text-white font-bold text-lg hover:bg-[#00A651] transition-colors shadow-2xl shadow-[#E3282B]/30"
          >
            {t.giveWater} <Heart size={18} fill="white" />
          </a>
        </div>
      </section>

      {/* AS SEEN IN — press features grid */}
      <section id="shop" className="py-24 md:py-28 bg-[#F4EDE0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold">
              <Newspaper size={14} /> {t.shopKicker}
            </div>
            <h2
              className="mt-4 text-4xl md:text-5xl leading-[1.1]"
              style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
              data-testid="stories-press-title"
            >
              {t.shopTitle}
            </h2>
            <p
              className="mt-5 text-base md:text-lg leading-relaxed text-[#2A1F15]/80"
              style={{ fontFamily: "'Merriweather', serif" }}
            >
              {t.shopBody}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-12 gap-6" data-testid="stories-press-grid">
            {/* Women of Solutions — biggest, hero card */}
            <article className="col-span-12 md:col-span-6 group relative overflow-hidden rounded-xl border-4 border-[#D4A93B] shadow-2xl bg-black" data-testid="stories-press-women">
              <div className="aspect-[4/5] md:aspect-[4/5]">
                <img src={PRESS_WOMEN_OF_SOLUTIONS} alt="Women of Solutions Magazine Special Women's Month Edition March 2026" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 bg-gradient-to-t from-black/95 via-black/70 to-transparent text-white">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4A93B] font-bold">{t.pressWomenKicker}</div>
                <h3 className="mt-2 text-xl md:text-2xl leading-tight" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>{t.pressWomenTitle}</h3>
              </div>
            </article>

            {/* Right column stack */}
            <div className="col-span-12 md:col-span-6 grid grid-cols-1 gap-6">
              <article className="group relative overflow-hidden rounded-xl border-4 border-[#2A1F15] shadow-xl bg-white" data-testid="stories-press-en">
                <div className="aspect-[16/10]">
                  <img src={PRESS_EN} alt="A survivor who became a global voice against female genital mutilation" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#E3282B] font-bold">{t.pressEnKicker}</div>
                  <h3 className="mt-2 text-base md:text-lg leading-snug" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>{t.pressEnTitle}</h3>
                </div>
              </article>

              <article className="group relative overflow-hidden rounded-xl border-4 border-[#00A651] shadow-xl bg-white" data-testid="stories-press-fr">
                <div className="aspect-[16/10]">
                  <img src={PRESS_FR} alt="Sélifa Boukari McGreevy — Briser le silence, sauver des vies" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#8C5A3C] font-bold">{t.pressFrKicker}</div>
                  <h3 className="mt-2 text-base md:text-lg leading-snug" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>{t.pressFrTitle}</h3>
                </div>
              </article>
            </div>
          </div>

          {/* Las Vegas Review-Journal — featured row */}
          <a
            href="https://www.reviewjournal.com/local/local-las-vegas/las-vegas-woman-fights-to-stop-female-genital-mutilation/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stories-press-cta"
            className="mt-8 group grid grid-cols-12 gap-6 items-center bg-white rounded-xl border-2 border-[#2A1F15]/10 hover:border-[#E3282B] hover:shadow-xl transition-all p-5 md:p-6 overflow-hidden"
          >
            <div className="col-span-12 md:col-span-3">
              <div className="aspect-[4/5] overflow-hidden rounded-lg">
                <img src={IMG_PRESS} alt="Las Vegas Review-Journal feature, 2017" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E3282B] font-bold">{t.pressLvKicker}</div>
              <h3 className="mt-2 text-2xl md:text-3xl leading-tight" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>{t.pressLvTitle}</h3>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#E3282B] group-hover:gap-3 transition-all">
                {t.shopGifts} <ArrowRight size={14} />
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-20 md:py-28 bg-[#8C5A3C] text-[#F4EDE0]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">{t.numbersKicker}</div>
          <div className="mt-6 flex items-baseline justify-center gap-4 flex-wrap">
            <span
              className="text-6xl md:text-8xl text-[#D4A93B]"
              style={{ fontFamily: "'Merriweather', serif", fontWeight: 900 }}
              data-testid="stories-weekly-number"
            >
              {t.numbersA}
            </span>
          </div>
          <p
            className="mt-6 max-w-xl mx-auto text-xl md:text-2xl leading-relaxed"
            style={{ fontFamily: "'Merriweather', serif" }}
          >
            {t.numbersB}
          </p>
          <a href="#" className="mt-8 inline-flex items-center gap-2 text-sm border-b-2 border-[#D4A93B] pb-1 text-[#D4A93B] font-semibold hover:text-white hover:border-white transition-colors">
            {t.learnMore} <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Silence Is Not An Option — 2026 advocacy campaign statement */}
      <section id="silence" className="py-24 md:py-32 bg-[#1A0F0A] text-[#F4EDE0] relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full blur-3xl opacity-20"
          style={{ background: "radial-gradient(circle, #E3282B 0%, transparent 60%)" }}
        />
        <div className="max-w-[900px] mx-auto px-6 md:px-10 relative">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold text-center">
            {t.silenceKicker}
          </div>
          <h2
            className="mt-6 text-5xl md:text-7xl leading-[1] text-center"
            style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
            data-testid="stories-silence-title"
          >
            {t.silenceHead}
          </h2>

          {/* Content warning */}
          <div className="mt-12 flex items-start gap-3 p-5 rounded-lg border border-[#E3282B]/40 bg-[#E3282B]/10">
            <AlertTriangle size={22} className="text-[#E3282B] shrink-0 mt-0.5" />
            <p className="text-sm md:text-base text-[#F4EDE0]/90 leading-relaxed">
              {t.silenceWarn}
            </p>
          </div>

          {/* Intro */}
          <p
            className="mt-10 text-lg md:text-xl leading-relaxed text-[#F4EDE0]/90 italic text-center"
            style={{ fontFamily: "'Merriweather', serif" }}
          >
            {t.silenceIntro}
          </p>

          {/* Read button + statement */}
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => setSilenceOpen((v) => !v)}
              data-testid="stories-silence-toggle"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#E3282B] text-white font-bold hover:bg-[#D4A93B] hover:text-black transition-colors"
            >
              {silenceOpen ? t.silenceHide : t.silenceRead}
              <ChevronDown
                size={18}
                className={`transition-transform ${silenceOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {silenceOpen && (
            <div
              className="mt-12 space-y-6 text-base md:text-lg leading-[1.85] text-[#F4EDE0]/95"
              style={{ fontFamily: "'Merriweather', serif" }}
              data-testid="stories-silence-statement"
            >
              {t.silenceBody.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}

          {/* Music video */}
          <div className="mt-16">
            <div className="text-center text-[11px] tracking-[0.3em] uppercase text-[#D4A93B] font-bold mb-6">
              {t.silenceVideoLabel}
            </div>
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black border border-[#D4A93B]/30">
              <video
                src={MUSIC_VIDEO}
                controls
                preload="metadata"
                playsInline
                poster={IMG_NEXT_GEN}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="stories-music-video"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Survivor Stories */}
      <SurvivorStoriesSection
        kicker={t.survivorsKicker}
        heading={t.survivorsHead}
        sub={t.survivorsSub}
        survivors={t.survivors}
        survivorLabel={t.survivorLabel}
        share={t.survivorShare}
      />

      {/* Pledge */}
      <section id="pledge" className="py-24 md:py-32 bg-[#F4EDE0]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="text-center">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold">{t.pledgeKicker}</div>
          </div>
          <div className="mt-12 space-y-6">
            {t.pledge.map((line, i) => (
              <div
                key={i}
                data-testid={`stories-pledge-line-${i}`}
                className="flex items-start gap-4 text-xl md:text-3xl leading-snug"
                style={{ fontFamily: "'Merriweather', serif", fontWeight: 500 }}
              >
                <CheckCircle2 size={28} className="text-[#00A651] shrink-0 mt-2" />
                <span>{line}</span>
              </div>
            ))}
          </div>
          <form
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="text"
              placeholder={t.firstName}
              data-testid="stories-pledge-first-name"
              className="px-5 py-4 rounded-lg border-2 border-[#2A1F15]/20 bg-white focus:outline-none focus:border-[#00A651]"
            />
            <input
              type="text"
              placeholder={t.lastName}
              data-testid="stories-pledge-last-name"
              className="px-5 py-4 rounded-lg border-2 border-[#2A1F15]/20 bg-white focus:outline-none focus:border-[#00A651]"
            />
            <input
              type="email"
              placeholder={t.email}
              data-testid="stories-pledge-email"
              className="px-5 py-4 rounded-lg border-2 border-[#2A1F15]/20 bg-white focus:outline-none focus:border-[#00A651] sm:col-span-2"
            />
            <button
              type="submit"
              data-testid="stories-sign-pledge"
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#E3282B] text-white font-bold hover:bg-[#00A651] transition-colors"
            >
              {t.signPledge} <ArrowRight size={16} />
            </button>
          </form>
          <div className="mt-5 text-center text-sm">
            <a href="#" className="underline underline-offset-4 decoration-[#8C5A3C] hover:text-[#8C5A3C] transition-colors">
              {t.readFull} &gt;
            </a>
          </div>

          {/* Share ribbon — recruit one friend */}
          <div className="mt-20 pt-12 border-t border-[#2A1F15]/15">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold">
                <Share2 size={14} /> {t.shareKicker}
              </div>
              <h3
                className="mt-4 text-3xl md:text-4xl leading-tight"
                style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
                data-testid="stories-share-title"
              >
                {t.shareTitle}
              </h3>
              <p className="mt-3 text-base md:text-lg text-[#2A1F15]/75 leading-relaxed">
                {t.shareSub}
              </p>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3" data-testid="stories-share-strip">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(t.shareMessage)}&url=${encodeURIComponent(SHARE_URL)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.shareX}
                data-testid="stories-share-x"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-black text-white font-semibold text-sm hover:scale-105 transition-transform shadow-md"
              >
                <Twitter size={16} fill="white" />
                {t.shareX}
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(t.shareMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.shareFacebook}
                data-testid="stories-share-facebook"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1877F2] text-white font-semibold text-sm hover:scale-105 transition-transform shadow-md"
              >
                <Facebook size={16} fill="white" />
                {t.shareFacebook}
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${t.shareMessage} ${SHARE_URL}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.shareWhatsapp}
                data-testid="stories-share-whatsapp"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:scale-105 transition-transform shadow-md"
              >
                <MessageCircle size={16} fill="white" />
                {t.shareWhatsapp}
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(t.shareEmailSubject)}&body=${encodeURIComponent(`${t.shareMessage}\n\n${SHARE_URL}`)}`}
                aria-label={t.shareEmail}
                data-testid="stories-share-email"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#8C5A3C] text-white font-semibold text-sm hover:scale-105 transition-transform shadow-md"
              >
                <Mail size={16} />
                {t.shareEmail}
              </a>
              <button
                type="button"
                onClick={handleCopyLink}
                aria-label={copied ? t.shareCopied : t.shareCopy}
                data-testid="stories-share-copy"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-[#2A1F15]/20 text-[#2A1F15] font-semibold text-sm hover:border-[#E3282B] hover:text-[#E3282B] transition-colors"
              >
                <CheckCircle2 size={16} className={copied ? "text-[#00A651]" : "text-[#2A1F15]/40"} />
                {copied ? t.shareCopied : t.shareCopy}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* DONATE TIERS — Stripe + PayPal */}
      <section id="donate" className="py-24 md:py-32 bg-[#F4EDE0] border-t border-[#2A1F15]/10">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold" data-testid="stories-donate-kicker">{t.donateKicker}</div>
            <h2
              className="mt-4 text-4xl md:text-6xl leading-[1.05]"
              style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
              data-testid="stories-donate-title"
            >
              {t.donateTitle}
            </h2>
            <p
              className="mt-6 text-base md:text-lg text-[#2A1F15]/80 leading-relaxed"
              style={{ fontFamily: "'Merriweather', serif" }}
            >
              {t.donateSub}
            </p>
          </div>

          {/* Coming-soon panel — email-to-give until Stripe & PayPal are live */}
          <div
            className="mt-14 max-w-3xl mx-auto rounded-xl bg-[#2A1F15] text-[#F4EDE0] p-7 md:p-10 shadow-2xl relative overflow-hidden"
            data-testid="stories-donate-coming-soon"
          >
            <div
              aria-hidden
              className="absolute -top-20 -right-20 h-[280px] w-[280px] rounded-full blur-3xl opacity-30"
              style={{ background: "radial-gradient(circle, #D4A93B 0%, transparent 60%)" }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">
                <AlertTriangle size={14} /> {t.donateSoonKicker}
              </div>
              <h3
                className="mt-4 text-2xl md:text-3xl leading-tight"
                style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
              >
                {t.donateSoonTitle}
              </h3>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-[#F4EDE0]/85">
                {t.donateSoonBody}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <a
                  href={`mailto:${CONTACT.email}?subject=${encodeURIComponent("I'd like to donate to Mother Hippo Foundation")}`}
                  data-testid="stories-donate-email-cta"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#E3282B] text-white font-bold hover:bg-[#00A651] transition-colors shadow-lg"
                >
                  <Mail size={16} /> {t.donateSoonCta}
                </a>
                <span className="text-sm text-[#F4EDE0]/70 italic">{t.donateSoonReply}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28 md:py-36 relative bg-[#2A1F15] text-[#F4EDE0] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={IMG_WORKSHOP_HERO} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <h2
            className="text-5xl md:text-7xl leading-[1]"
            style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
          >
            <span className="text-[#D4A93B]">{t.giveClean}</span> {t.giveStrong}
            <br />{t.giveEnd}
          </h2>
          <a
            href="#donate"
            data-testid="stories-final-cta"
            className="mt-10 inline-flex items-center gap-3 px-10 py-5 rounded-full bg-[#E3282B] text-white font-bold text-lg hover:bg-[#00A651] transition-colors shadow-2xl"
          >
            {t.persistent} <Heart size={18} fill="white" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />

      {/* Persistent Donate Button */}
      <FloatingDonate />
    </div>
  );
}
